import * as React from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, FileText, Search, UserRound } from 'lucide-react'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { Skeleton, SkeletonBox } from 'skullet/react'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { formatDateTimeRange, formatTimestamp } from '@/lib/date-utils'
import { cn, formatFullName } from '@/lib/utils'
import { useInquiry, useInfiniteLeadsList } from '@/stores/leads.store'

import { QuoteBuilderInline } from '@/components/quotes'
import { InquiryStageBadge } from '@/components/shared/enum-badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { InquiryLiteSchema } from '@/gen/cortado/v1/inquiries_pb'
import { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'

export const Route = createFileRoute('/_app/_home/quotes/new')({
  component: NewQuotePage,
})

const SAMPLE_LEADS = [
  { contactFirstName: 'Jordan', contactLastName: 'Ellis', contactCompanyName: 'Harbor & Vine Catering' },
  { contactFirstName: 'Priya', contactLastName: 'Ramanathan' },
  { contactFirstName: 'Marcus', contactLastName: 'Okafor', contactCompanyName: 'Northfield Events' },
  { contactFirstName: 'Lena', contactLastName: 'Hart' },
  { contactFirstName: 'Sam', contactLastName: 'Whitaker', contactCompanyName: 'Union Station Loft' },
  { contactFirstName: 'Ana', contactLastName: 'Duarte' },
].map((lead, i) =>
  create(InquiryLiteSchema, {
    ...lead,
    id: `sample-${i}`,
    stage: InquiryStageEnum.INQUIRY_STAGE_NEW,
    firstEventDate: timestampFromDate(new Date(2026, 6, 12 + i, 17)),
  }),
)

export function NewQuotePage() {
  const navigate = useNavigate()
  const breadcrumbs = React.useMemo(() => [
    { label: 'Home', href: '/home' },
    { label: 'Quotes', href: '/quotes' },
    { label: 'New Quote' },
  ], [])

  useBreadcrumbFromRoute(breadcrumbs)

  const [selectedInquiryId, setSelectedInquiryId] = React.useState<string>()
  const [isLeadPickerOpen, setIsLeadPickerOpen] = React.useState(true)
  const [pendingInquiryId, setPendingInquiryId] = React.useState<string>()
  const [search, setSearch] = React.useState('')
  // Debounce the query so we hit the server as the user pauses typing, not on
  // every keystroke. Empty search returns the most recent inquiries (first page).
  const debouncedSearch = useDebouncedValue(search.trim(), 300)
  const {
    inquiries,
    isLoading,
    isFetching,
    isPlaceholderData,
    hasLoaded,
  } = useInfiniteLeadsList({ search: debouncedSearch })
  const { inquiry, isLoading: selectedInquiryLoading } = useInquiry(selectedInquiryId)
  const isSearchLoading =
    search.trim() !== debouncedSearch ||
    isLoading ||
    !hasLoaded ||
    (isFetching && isPlaceholderData)
  const selectedLeadSummary = inquiries.find((lead) => lead.id === selectedInquiryId)
  const selectedContact = inquiry?.contact
  const selectedLeadName = selectedLeadSummary
    ? formatFullName(selectedLeadSummary.contactFirstName, selectedLeadSummary.contactLastName, 'Unknown contact')
    : selectedContact
      ? formatFullName(selectedContact.firstName, selectedContact.lastName, 'Unknown contact')
      : 'Selected lead'
  const selectedCompanyName = selectedLeadSummary?.contactCompanyName ?? selectedContact?.companies?.[0]?.name
  const selectedLeadStage = selectedLeadSummary?.stage ?? inquiry?.stage
  const pendingLeadSummary = inquiries.find((lead) => lead.id === pendingInquiryId)
  const pendingLeadName = pendingLeadSummary
    ? formatFullName(pendingLeadSummary.contactFirstName, pendingLeadSummary.contactLastName, 'the new lead')
    : 'the new lead'

  const selectLead = (inquiryId: string) => {
    if (selectedInquiryId && inquiryId !== selectedInquiryId) {
      setPendingInquiryId(inquiryId)
      return
    }

    setSelectedInquiryId(inquiryId)
    setIsLeadPickerOpen(false)
  }

  const confirmLeadChange = () => {
    if (!pendingInquiryId) return
    setSelectedInquiryId(pendingInquiryId)
    setPendingInquiryId(undefined)
    setIsLeadPickerOpen(false)
  }

  const handleCreateSuccess = (newQuoteId?: string) => {
    if (newQuoteId) {
      navigate({ to: '/quotes/$id', params: { id: newQuoteId } })
    } else {
      navigate({ to: '/quotes' })
    }
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display-heading text-wrap-balance text-2xl font-semibold tracking-tight">New Quote</h1>
          <p className="text-sm text-muted-foreground">
            {selectedInquiryId && !isLeadPickerOpen
              ? 'Review each step, then create the quote.'
              : 'Select an existing lead before creating the quote.'}
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/quotes">
            <ArrowLeft className="h-4 w-4" />
            Back to Quotes
          </Link>
        </Button>
      </div>

      <div className={cn(
        'grid gap-4',
        (!selectedInquiryId || isLeadPickerOpen) && 'xl:grid-cols-[360px_minmax(0,1fr)]',
      )}>
        <Card className={cn('h-fit', selectedInquiryId && !isLeadPickerOpen && 'border-primary/20 bg-card/70 shadow-xs')}>
          {selectedInquiryId && !isLeadPickerOpen ? (
            <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <UserRound className="size-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Quote For</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold">
                      {selectedLeadName}
                    </p>
                    {selectedLeadStage !== undefined && <InquiryStageBadge stage={selectedLeadStage} />}
                  </div>
                  {selectedCompanyName && (
                    <p className="truncate text-sm text-muted-foreground">{selectedCompanyName}</p>
                  )}
                </div>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => setIsLeadPickerOpen(true)}>
                Change Lead
              </Button>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Select Lead</CardTitle>
                <CardDescription>Quotes must be tied to an existing inquiry.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input
                    name="lead-search"
                    autoComplete="off"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search leads…"
                    className="pl-9"
                  />
                </div>

                <Skeleton
                  data={isSearchLoading ? null : inquiries}
                  sample={SAMPLE_LEADS}
                  id="quotes:new:leads"
                  label="Loading leads"
                  empty={!isSearchLoading && inquiries.length === 0}
                  emptyMessage={
                    <Empty className="border border-dashed py-12">
                      <EmptyMedia variant="icon">
                        <FileText className="h-5 w-5" aria-hidden="true" />
                      </EmptyMedia>
                      <EmptyDescription>No leads match this search.</EmptyDescription>
                    </Empty>
                  }
                >
                  {(inquiries) => (
                    <div className="max-h-[640px] space-y-2 overflow-auto pr-1">
                      {inquiries.map((lead) => {
                        const isSelected = selectedInquiryId === lead.id
                        const name = formatFullName(lead.contactFirstName, lead.contactLastName, 'Unknown contact')

                        return (
                          <button
                            key={lead.id}
                            type="button"
                            onClick={() => selectLead(lead.id)}
                            className={cn(
                              'w-full rounded-lg border p-3 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 active:scale-[0.99] motion-reduce:transform-none',
                              isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'bg-card',
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate font-medium">{name}</p>
                                {lead.contactCompanyName && (
                                  <p className="truncate text-sm text-muted-foreground">{lead.contactCompanyName}</p>
                                )}
                              </div>
                              <InquiryStageBadge stage={lead.stage} />
                            </div>
                            <div className="mt-3 text-xs text-muted-foreground">
                              {lead.firstEventDate
                                ? formatDateTimeRange(lead.firstEventDate, undefined, lead.firstEventTimezone)
                                : `Created ${formatTimestamp(lead.createdAt)}`}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </Skeleton>
              </CardContent>
            </>
          )}
        </Card>

        <div className="min-w-0">
          {!selectedInquiryId ? (
            <Empty className="min-h-[420px] border border-dashed py-12">
              <EmptyMedia variant="icon">
                <FileText className="h-6 w-6" />
              </EmptyMedia>
              <EmptyDescription>Select a lead to open the quote builder.</EmptyDescription>
            </Empty>
          ) : selectedInquiryLoading || !inquiry ? (
            <Skeleton loading label="Loading lead">
              <SkeletonBox width="100%" height={420} radius="0.5rem" />
            </Skeleton>
          ) : (
            <QuoteBuilderInline
              key={selectedInquiryId}
              inquiry={inquiry}
              onCancel={() => navigate({ to: '/quotes' })}
              onSuccess={handleCreateSuccess}
            />
          )}
        </div>
      </div>

      <AlertDialog
        open={!!pendingInquiryId}
        onOpenChange={(open) => {
          if (!open) setPendingInquiryId(undefined)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change quote to {pendingLeadName}?</AlertDialogTitle>
            <AlertDialogDescription>
              The title, events, line items, and payment schedule in this draft will reset so the new quote starts clean.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Current Lead</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeadChange}>Change and Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** Returns `value` after it has stopped changing for `delayMs` milliseconds. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState(value)

  React.useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timeoutId)
  }, [delayMs, value])

  return debouncedValue
}
